<script setup>
import { Icon } from '@iconify/vue';
import { computed } from 'vue';

const props = defineProps({
    title: {
        type: String,
        required: true
    },
    subtitle: {
        type: String,
        default: ''
    },
    icon: {
        type: String,
        default: ''
    },
    color: {
        type: String,
        default: 'blue' // Options: blue, rose, green, purple, amber, slate, indigo
    },
    hideSubtitleOnMobile: {
        type: Boolean,
        default: false
    },
    hideIconOnMobile: {
        type: Boolean,
        default: false
    },
    wrapTabsOnMobile: {
        type: Boolean,
        default: true
    },
    actionsInTabsOnMobile: {
        type: Boolean,
        default: false
    },
    forceActionsOnTop: {
        type: Boolean,
        default: false
    },
    actionsInTabs: {
        type: Boolean,
        default: false
    },
    actionsOnBottom: {
        type: Boolean,
        default: false
    },
    tabs: {
        type: Array,
        default: () => []
    },
    modelValue: {
        type: [Number, String],
        default: null
    },
    actionsOnBottomOnMobile: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'change']);

const selectTab = (tab) => {
    emit('update:modelValue', tab.id);
    emit('change', tab);
};

// Tailwind requiere clases completas para el compilador (JIT), usamos un mapa en lugar de clases dinámicas.
const colorMap = {
    blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    rose: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400',
    green: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    purple: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    slate: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
};

const iconClass = computed(() => colorMap[props.color] || colorMap.blue);
</script>

<!-- hideSubtitleOnMobile: Ocultara el subtitulo en movil -->
<!-- actionsOnBottomOnMobile: mostrara los botones a la derecha -->

<template>
    <!-- Contenedor Principal Flex (Rústico pero súper funcional) -->
    <div class="min-h-[70px] xl:min-h-[80px] ">
        <div v-if="title || subtitle || icon || $slots.actions" class="w-full justify-between gap-2" :class="[
            ($slots.bottom || (tabs && tabs.length > 0)) ? 'content-between' : 'content-center',
            !(title || subtitle || icon) ? 'hidden' : 'flex items-center min-h-[70px] xl:min-h-[80px]'
        ]">
            <div class="w-full min-h-[70px] xl:min-h-[80px] flex gap-3" :class="[
                (($slots.bottom || (tabs && tabs.length > 0)) && (title || subtitle || icon)) ? 'flex-col items-stretch justify-center' : 'items-center',
                { 'xl:flex-col xl:items-stretch': $slots.bottom || (tabs && tabs.length > 0) }
            ]">
                <!-- Área 1: Título y Subtítulo -->
                <div v-if="title || subtitle || icon" class="flex justify-between" :class="{ ' min-h-[70px] xl:min-h-[80px] items-center': !($slots.bottom || (tabs && tabs.length > 0)) }">
                    <div class="flex items-center gap-3" :class="[actionsOnBottomOnMobile ? 'w-full lg:flex-1' : 'flex-1',]">
                        <!-- Box de ícono -->
                        <div v-if="icon" class="p-2.5 rounded-2xl shadow-sm shrink-0" :class="[iconClass, { 'hidden xl:block': hideIconOnMobile }]">
                            <Icon :icon="icon" class="w-7 h-7" />
                        </div>

                        <!-- Textos -->
                        <div v-if="title || subtitle">
                            <h2 v-if="title" class="font-bold text-md text-slate-800 dark:text-white leading-tight" :class="(subtitle || (tabs && tabs.length > 0)) ? 'lg:text-md' : 'lg:text-[1.15rem]'">
                                {{ title }}
                            </h2>
                            <p v-if="subtitle" class="text-xs text-slate-500 dark:text-slate-400 mt-1" :class="{ 'hidden xl:block': hideSubtitleOnMobile }">
                                {{ subtitle }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Área 3: Acciones (Botones) - En su propio espacio exclusivo -->
            <div v-if="$slots.actions" class="gap-2 shrink-0" :class="[
                (actionsInTabs || actionsOnBottom || !(title || subtitle || icon)) 
                    ? 'hidden' 
                    : (actionsInTabsOnMobile ? 'hidden xl:flex xl:items-center' : 'flex items-center')
            ]">
                <slot name="actions" />
            </div>

        </div>
        <!-- Área 2: Tabs (Siempre anclados al fondo) -->
        <div v-if="$slots.bottom || (tabs && tabs.length > 0)" class="w-full xl:mt-auto" :class="[
            { 'pt-1 pb-2 xl:pb-0 xl:pt-0': (title || subtitle || icon) },
            { 'py-3 sm:py-0': tabs && tabs.length > 3 && !(title || subtitle || icon) }
        ]">
            <!-- Wrapper para Tabs y Acción -->
            <div class="w-full" :class="[
                (actionsOnBottom || !(title || subtitle || icon)) && !actionsInTabs
                    ? (actionsInTabsOnMobile ? 'block xl:flex xl:items-center xl:justify-between xl:gap-3' : 'flex items-center justify-between gap-3')
                    : 'block'
            ]">
                
                <!-- Contenedor de TABS -->
                <div class="flex-1 min-w-0 flex" :class="[
                    !(title || subtitle || icon) 
                        ? 'min-h-[70px] items-center xl:min-h-[80px] xl:items-end' 
                        : 'items-center'
                ]">
                    <div v-if="tabs && tabs.length > 0" class="flex relative bottom-0 overflow-x-auto overflow-y-hidden w-full custom-scrollbar pb-1 gap-1 md:gap-3 xl:pb-0 xl:pt-1 z-50 -mb-[1px]" :class="wrapTabsOnMobile ? 'flex-wrap' : 'flex-nowrap xl:flex-wrap'">
                        <template v-for="tab in tabs" :key="tab.id">
                            <button v-if="tab.show !== false" @click="selectTab(tab)" class="relative text-xs xl:text-sm font-bold transition-colors flex items-center justify-center shrink-0 px-3 py-1.5 rounded-lg xl:px-6 xl:py-2.5 xl:rounded-none xl:rounded-t-lg border" :class="[
                                modelValue === tab.id
                                    ? 'bg-white text-slate-800 shadow-sm xl:shadow-none border-slate-200 xl:border-b-transparent xl:z-10'
                                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border-slate-200 xl:border-b-slate-200 xl:z-0'
                            ]">
                                <span class="flex items-center gap-2">
                                    <Icon v-if="tab.icon" :icon="tab.icon" class="hidden xl:block w-4 h-4" />
                                    {{ tab.label }}
                                </span>

                                <!-- Línea roja para el tab activo (solo visible en desktop xl+) -->
                                <div v-if="modelValue === tab.id" class="hidden xl:block absolute -bottom-[1px] left-4 right-4 h-[2px] bg-rose-600"></div>
                            </button>
                        </template>
                        <!-- BOTÓN POSITION C (Inside Tabs) -->
                        <div v-if="$slots.actions" class="shrink-0" :class="[
                            actionsInTabs 
                                ? 'flex items-center pb-0' 
                                : (actionsInTabsOnMobile ? 'flex items-center pb-1 xl:hidden' : 'hidden')
                        ]">
                            <slot name="actions" />
                        </div>
                    </div>
                </div>

                <!-- BOTÓN POSITION B (Bottom Far Right) -->
                <div v-if="$slots.actions && !actionsInTabs" class="shrink-0 items-center" :class="[
                    (actionsOnBottom || !(title || subtitle || icon))
                        ? (actionsInTabsOnMobile ? 'hidden xl:flex' : 'flex')
                        : 'hidden',
                    !(title || subtitle || icon) ? 'min-h-[70px] xl:min-h-[80px]' : ''
                ]">
                    <slot name="actions" />
                </div>

            </div>
            
            <slot name="bottom" />
        </div>
    </div>
</template>

<!-- 
================================================================================
📄 DOCUMENTACIÓN DEL COMPONENTE PAGEHEADER
================================================================================
Un encabezado de página altamente personalizable y responsivo con soporte para 
títulos, subtítulos, íconos, botones de acción (slots) y pestañas (tabs).

PROPIEDADES (Props):
--------------------------------------------------------------------------------
- title (String, Obligatorio): El título principal de la página.
- subtitle (String, Opcional): El texto secundario debajo del título. Si no se provee, el título aumentará su tamaño ligeramente en computadora.
- icon (String, Opcional): Nombre del ícono (Iconify) que aparecerá a la izquierda del título (ej: 'tabler:settings').
- color (String, Opcional): Color del ícono. Opciones: blue, rose, green, purple, amber, slate, indigo. Default: 'blue'.
- hideSubtitleOnMobile (Boolean, Opcional): Oculta el subtítulo en dispositivos móviles (menores a 'xl'). Default: false.
- hideIconOnMobile (Boolean, Opcional): Oculta el ícono del título en móviles. Default: false.
- wrapTabsOnMobile (Boolean, Opcional): Si es true, las pestañas bajarán de línea (flex-wrap) si no caben en móviles. Si es false, aparecerá una barra de scroll horizontal. Default: true.
- actionsInTabs (Boolean, Opcional): Si es true, integra los botones (#actions) directamente dentro del contenedor deslizable de las pestañas, al final de la fila. Default: false.
- actionsOnBottom (Boolean, Opcional): Si es true, ancla los botones (#actions) en la parte inferior derecha a nivel de las pestañas (separado del scroll). Si no hay título, se activa por defecto. Default: false.
- actionsInTabsOnMobile (Boolean, Opcional): Igual que actionsInTabs, pero aplica únicamente en dispositivos móviles. Default: false.
- tabs (Array, Opcional): Arreglo de pestañas. Formato: { id: 1, label: 'General', icon: 'tabler:apps' }.
- modelValue (Number|String, Opcional): Usado con v-model para controlar el ID del tab activo.

SLOTS:
--------------------------------------------------------------------------------
- #actions: Para inyectar botones de acción a la derecha (o abajo en móvil) del encabezado.
- #bottom: Espacio adicional justo debajo de los tabs por si se requiere contenido extra.

EVENTOS (@):
--------------------------------------------------------------------------------
- update:modelValue: Emitido al cambiar de pestaña para actualizar el v-model.
- change(tab): Emitido al hacer clic en un tab. Devuelve el objeto completo de la pestaña seleccionada.
================================================================================
-->
